<template>
  <div :class="[$style.contentGrid, 'SubPage__contentGrid']">
    <ElasticSearch :config="config" :model="elasticSearchModel" @submitted="startNewSearch" />
    <p v-if="hint !== hintEnum.EMPTY" :class="$style.info">
      <em class="hlt1">{{ config.textPrefix }}: </em>
      <span v-html="computedHint" />
    </p>
    <p v-if="!!articleNumberSearchResult.articleNumber" :class="$style.info">
      <em class="hlt1">{{ config.hintPrefix }}: </em>
      <span>{{
        `"${articleNumberSearchResult.articleNumber}" ${config.hintArticle} "${articleNumberSearchResult.productType}"`
      }}</span>
    </p>
    <p v-if="!!articleNumberSearchResult.productType" :class="$style.info">
      <em class="hlt1">{{ config.hintPrefix }}: </em>
      <span v-html="config.hintArticleInShop" />
    </p>
    <p v-if="totalHits > 0" :class="$style.info" v-html="resultsText" />
    <div class="columns is-desktop">
      <div class="column is-full is-one-third-desktop">
        <ResultFilter
          :filterResetText="config.filterResetText"
          :filters="filters"
          :range-size="rangeSize"
          :results="results"
          :terms="terms"
          @filtered="displayResults"
        />
      </div>
      <div class="column is-full is-two-thirds-desktop">
        <div :class="[$style.resultListing, 'columns is-multiline']">
          <div
            v-if="!!showTeaserArticle && !!standardArticleResult"
            class="column is-full is-half-tablet"
          >
            <ResultTeaserArticle
              :article-number="articleNumberSearchResult.articleNumber || ''"
              :bg-img-url="config.bgImgUrl"
              :item-info-url="itemInfoUrl"
              :result="standardArticleResult as SearchHit"
            />
          </div>
          <div
            v-for="(result, index) in displayedResults"
            :key="index"
            class="column is-full is-half-tablet"
          >
            <ResultTeaser
              :key="`resultTeaserKey_${JSON.stringify(result)}`"
              :result="result"
              :search-microsite="config.searchMicrosite"
              :shop-base-url="config.shopBaseUrl"
              :terms="terms"
              :text-country="config.textCountry"
              :text-employment="config.textEmployment"
              :text-location="config.textLocation"
              :text-target-group="config.textTargetGroup"
              :textfield-of-activity="config.textfieldOfActivity"
              :web-base-url="config.webBaseUrl"
            />
          </div>
        </div>
        <div :class="$style.loading">
          <Loading :is-loaded="!loading" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import VueSecureHTML from 'vue-html-secure';
import ElasticSearch from '../ElasticSearch/ElasticSearch.vue';
import ResultFilter from '../ResultFilter/ResultFilter.vue';
import ResultTeaser from '../ResultTeaser/ResultTeaser.vue';
import Loading from '../Loading/Loading.vue';
import ResultTeaserArticle from '../ResultTeaserArticle/ResultTeaserArticle.vue';
import { computed, ref, watch } from 'vue';
import { useStoreConfiguration } from '../../store/configuration/configuration';
import { post } from '../../xhr/post';
import withBaseUrl from '../../xhr/withBaseUrl';
import type { WindowWika } from '../../types/window.ts';
import type { SearchCategory, SearchHit } from '../../types/search.ts';

const _window = window as unknown as WindowWika;

const hintEnum = {
  MIN_LENGTH: 'MIN_LENGTH',
  NO_RESULT: 'NO_RESULT',
  NONE: 'NONE',
  EMPTY: 'EMPTY',
};

type Filter = {
  name: string;
  label: string;
  hits: number;
};

type ArticleNumberSearchResult = {
  articleNumber: string | null;
  productType: string | null;
};

const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
  rangeSize: {
    type: Number,
    default: 25,
  },
});

const storeConfiguration = useStoreConfiguration();
const computedCountry = computed(() => {
  return (
    storeConfiguration?.caasConfig?.searchCountry ??
    _window?.searchCountry ??
    storeConfiguration?.caasConfig?.currentCountry ??
    _window?.currentCountry ??
    storeConfiguration?.caasConfig?.locale
  );
});

const terms = ref<string[]>([]);
const displayedResults = ref<SearchHit[]>([]);
const totalHits = ref(0);
const results = ref<SearchHit[]>();
const standardArticleResult = ref<SearchHit>();
const filters = ref<Filter[]>([]);
const range = ref({
  from: 0,
});
const loading = ref(false);
const useCategorySearch = ref(false);
const articleNumberSearchResult = ref<ArticleNumberSearchResult>({
  articleNumber: null,
  productType: null,
});
const memorizedLabels = ref({}); // in case of scolling result has no labels, so we need this cache
const elasticSearchModel = ref<string[]>([]);
const hint = ref(hintEnum.EMPTY);

const caasItemInfoUrl = ref<string | undefined>(undefined);
const showTeaserArticle = ref(false);

const itemInfoUrl = computed(() => {
  return caasItemInfoUrl.value || _window.getItemInfoUrl;
});

const disableExternalDocuments = _window.disableExternalDocuments;

const resultsText = computed(() => {
  return props.config.resultsText.replace(/{{result}}/g, `<strong>${totalHits.value}</strong>`);
});

const computedHint = computed(() => {
  if (hint.value === hintEnum.MIN_LENGTH) {
    return props.config.textMinLength;
  } else {
    return hint.value === hintEnum.NO_RESULT
      ? props.config.textNoResult.replace(
          /{{searchPhrase}}/g,
          `<strong>${VueSecureHTML.escapeHTML(terms.value.join(' '))}</strong>`,
        )
      : '';
  }
});

const getPotentialArticleNumber = () => {
  const articleNumberRegEx = new RegExp('^[\\d]{6,8}$', 'gm');
  return terms.value.find((term) => !!articleNumberRegEx.exec(term));
};

const initSearch = () => {
  const queries = _window.location.search.substring(1).split('&');
  let queryString: string = '';
  const decodedTerms: string[] = [];

  if (queries) {
    for (const query of queries) {
      if (query.includes('wika-search')) {
        queryString = query.split('=')[1];
        terms.value = queryString.split('+') as [];

        for (const term of terms.value) {
          decodedTerms.push(decodeURI(term));
        }

        elasticSearchModel.value = decodedTerms;

        if (decodedTerms.length === 1 && decodedTerms[0].length < 2) {
          hint.value = hintEnum.MIN_LENGTH;
          return;
        }

        getSearchResults(elasticSearchModel.value);
      }
    }
  }
};

const initScroll = () => {
  const distanceToStartLoad = 300;

  _window.onscroll = () => {
    let bottomOfWindow =
      document.documentElement.clientHeight +
        document.documentElement.scrollTop +
        distanceToStartLoad >=
      document.documentElement.scrollHeight;

    if (bottomOfWindow && !loading.value && !useCategorySearch.value) {
      range.value.from = range.value.from + props.rangeSize;

      // ensure that getSearchResults is fired only when not all of the results are displayed
      displayedResults.value.length < totalHits.value && getSearchResults(terms.value, true);
    }
  };
};

const processSearch = (data) => {
  totalHits.value = data.totalhits;
  if (_window.dataLayer) {
    trackSearchResult();
  }

  hint.value = totalHits.value === 0 ? hintEnum.NO_RESULT : hintEnum.EMPTY;

  displayedResults.value = [];
  setDisplayResults(data.categoryhits);
  setFilters(data.categoryhits);

  results.value = displayedResults.value;

  standardArticleResult.value = displayedResults.value.find((result) =>
    result._source.articles?.includes(terms.value[0]),
  );

  loading.value = false;
  initScroll();
};

const trackSearchResult = () => {
  const params = {
    'WT.oss': terms.value.join(' '),
    'WT.oss_r': totalHits.value?.toString(),
  };

  _window.wtTrackPage && _window.wtTrackPage(params);
};

const updateSearch = (data) => {
  setDisplayResults(data.categoryhits);
  setFilters(data.categoryhits);

  results.value = displayedResults.value;
  loading.value = false;
};

const setDisplayResults = (categoryhits) => {
  const isHitsArray = Array.isArray(categoryhits);

  if (isHitsArray) {
    for (const category of categoryhits) {
      if (
        getPotentialArticleNumber() &&
        (category.name === 'products' || category.name === 'shop')
      ) {
        setArticleNumberSearchResult([...category.result.hits.hits]);
      }
      displayedResults.value.push(...category.result.hits.hits);
    }
  }
  displayedResults.value.sort((a, b) => b._score - a._score);
};

const setFilters = (categoryhits: SearchCategory[] | SearchCategory) => {
  filters.value = [];

  if (Array.isArray(categoryhits)) {
    for (const category of categoryhits) {
      if (category.label) {
        memorizedLabels.value[category.name] = category.label;
      }

      filters.value.push({
        name: category.name,
        label: memorizedLabels.value[category.name] || category.name,
        hits: category.result.hits.total.value,
      });
    }
  } else {
    filters.value.push({
      name: 'produkte',
      label: memorizedLabels.value['produkte'] || 'produkte',
      hits: 1,
    });
  }
};

const emptyResultList = () => {
  terms.value = [];
  displayedResults.value = [];
  totalHits.value = 0;
  results.value = [];
  standardArticleResult.value = undefined;
  filters.value = [];
  useCategorySearch.value = false;
  range.value.from = 0;
};

const startNewSearch = (newTerms) => {
  if (newTerms.length === 1 && newTerms[0].length < 2) {
    emptyResultList();

    hint.value = hintEnum.MIN_LENGTH;

    return;
  }

  range.value.from = 0;
  useCategorySearch.value = false;

  articleNumberSearchResult.value = {
    articleNumber: null,
    productType: null,
  };

  getSearchResults(newTerms);
};

const getSearchResults = (newTerms: string[], onScroll?: boolean) => {
  loading.value = true;
  terms.value = newTerms;

  if (!storeConfiguration.config) {
    return;
  }

  post(
    withBaseUrl({
      config: storeConfiguration.config,
      type: 'search',
      url: storeConfiguration.config.caas_combinedSearchUrl,
    }),
    {
      // global
      container: storeConfiguration.config.pt_search_container,
      shopContainer: storeConfiguration.config.pt_search_shop_container,
      language: storeConfiguration?.caasConfig?.searchCountry
        ? storeConfiguration.caasConfig.language.replace('EN-US', 'EN')
        : storeConfiguration?.caasConfig?.language,
      country: computedCountry.value,
      instance: storeConfiguration?.caasConfig?.instance || _window.instance,
      disableExternalDocuments,
      // payload
      word: newTerms,
      from: range.value.from,
      size: props.rangeSize,
    },
  )
    .then((responseData) => {
      if (onScroll) {
        updateSearch(responseData);
      } else {
        processSearch(responseData);
      }
    })
    .catch((postError) => {
      console.error(postError);
    });
};

const displayResults = (newDisplayedResults) => {
  if (results.value?.length === newDisplayedResults.length) {
    useCategorySearch.value = false;

    initScroll();
  } else {
    useCategorySearch.value = true;
  }

  displayedResults.value = newDisplayedResults;
};

const setArticleNumberSearchResult = (products) => {
  for (const product of products) {
    if (product._source.standardarticle || product._source.standardArticle) {
      return (articleNumberSearchResult.value = {
        articleNumber: getPotentialArticleNumber() ?? null,
        productType: (product._source.productType || product._source.producttype) ?? null,
      });
    }
  }
};

watch(articleNumberSearchResult, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    showTeaserArticle.value = newValue.articleNumber ? !!newValue.articleNumber : false;
  }
});

watch(
  storeConfiguration,
  () => {
    if (storeConfiguration.config) {
      initSearch();
      caasItemInfoUrl.value = storeConfiguration.config.caas_getItemInfoUrl;
    }
  },
  { immediate: true },
);
</script>

<style module lang="scss">
@forward './ResultList.scss';
</style>
